import { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Button,
  useTheme,
} from '@mui/material';
import {
  BudgetEditItem,
  BudgetItemOption,
} from '@models/drawRequests/budgetEditItem';
import { BudgetItemResponse } from '@models/drawRequests/budgetItemResponse';
import useDrawRequestQueryApi from '@hooks/api/useDrawRequestQueryApi';
import { useApp } from '@context/AppContext';
import BudgetEditTable from './BudgetEditTable';
import BudgetEditCards from './BudgetEditCards';

interface BudgetGridEditViewProps {
  sourceRecId: string;
  initialItems?: BudgetEditItem[];
  onItemsChange?: (items: BudgetEditItem[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  showSubmitButton?: boolean;
  isFormValid?: boolean;
  onSubmitRequest?: () => void;
  isSubmitting?: boolean;
  buttonText?: string;
  submittingText?: string;
}

export default function BudgetGridEditView({
  sourceRecId,
  initialItems = [],
  onItemsChange,
  onValidationChange,
  showSubmitButton = false,
  isFormValid = false,
  onSubmitRequest,
  isSubmitting = false,
  buttonText = 'Submit Request',
  submittingText = 'Submitting...',
}: BudgetGridEditViewProps) {
  const theme = useTheme();
  const { isSmallDesktop } = useApp();
  const [items, setItems] = useState<BudgetEditItem[]>(initialItems);

  const {
    data: budgetItemsData,
    isLoading,
    error,
  } = useDrawRequestQueryApi.useFetchBudgetItems(sourceRecId);

  const budgetItemOptions = useMemo((): BudgetItemOption[] => {
    if (!budgetItemsData) return [];

    return budgetItemsData.map((item: BudgetItemResponse) => ({
      value: item.recId,
      label: `${item.account} - ${item.description} | ${(item.remainingBudgetAmount ?? 0) < 0 ? '-$' : '$'}${Math.abs(item.remainingBudgetAmount ?? 0).toFixed(2)}`,
      remainingAmount: item.remainingBudgetAmount,
      percentComplete: item.pctCompleted || 0,
    }));
  }, [budgetItemsData]);

  const drawTotal = useMemo(() => {
    return items.reduce((total, item) => total + (item.requestAmount || 0), 0);
  }, [items]);

  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      setItems(initialItems);
    }
  }, [initialItems]);

  useEffect(() => {
    if (
      items.length === 0 &&
      budgetItemOptions.length > 0 &&
      (!initialItems || initialItems.length === 0)
    ) {
      const defaultItems: BudgetEditItem[] = [
        {
          id: '1',
          budgetItem: '',
          remainingAmount: 0,
          requestAmount: 0,
          percentComplete: 0,
          isNew: true,
        },
      ];
      setItems(defaultItems);
    }
  }, [budgetItemOptions, initialItems]);

  const handleItemChange = (
    index: number,
    field: keyof BudgetEditItem,
    value: any
  ) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    if (field === 'budgetItem') {
      const selectedOption = budgetItemOptions.find(
        option => option.value === value
      );
      if (selectedOption) {
        updatedItems[index].remainingAmount = selectedOption.remainingAmount;
        updatedItems[index].percentComplete = selectedOption.percentComplete;
      }
    }

    setItems(updatedItems);
    onItemsChange?.(updatedItems);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
      onItemsChange?.(updatedItems);
    }
  };

  const handleAddItem = () => {
    const newItem: BudgetEditItem = {
      id: Date.now().toString(),
      budgetItem: '',
      remainingAmount: 0,
      requestAmount: 0,
      percentComplete: 0,
      isNew: true,
    };

    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onItemsChange?.(updatedItems);
  };

  const renderBudgetEditCards = () => (
    <BudgetEditCards
      items={items}
      budgetItemOptions={budgetItemOptions}
      onItemChange={handleItemChange}
      onRemoveItem={handleRemoveItem}
      onAddItem={handleAddItem}
      onValidationChange={onValidationChange}
    />
  );

  const renderBudgetEditTable = () => (
    <BudgetEditTable
      items={items}
      budgetItemOptions={budgetItemOptions}
      onItemChange={handleItemChange}
      onRemoveItem={handleRemoveItem}
      onAddItem={handleAddItem}
      onValidationChange={onValidationChange}
    />
  );

  const renderLoadingState = () => (
    <Paper
      component="section"
      elevation={1}
      aria-label="Loading budget items"
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
      }}
    >
      <CircularProgress aria-label="Loading budget items" />
    </Paper>
  );

  const renderErrorState = () => (
    <Paper
      component="section"
      elevation={1}
      aria-label="Error loading budget items"
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Alert severity="error" role="alert" aria-live="polite">
        Failed to load budget items. Please try again.
      </Alert>
    </Paper>
  );

  const renderSubmitButton = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        mt: 0,
        gap: 2,
      }}
    >
      <Typography
        component="div"
        role="text"
        aria-label={`Total draw amount: $${drawTotal.toLocaleString()}`}
        sx={{
          fontFamily: 'Epilogue',
          fontSize: '20px',
          fontWeight: 500,
          lineHeight: '150%',
          color: theme.palette.text.primary,
        }}
      >
        Draw Total = ${drawTotal.toLocaleString()}
      </Typography>

      <Button
        onClick={isFormValid && !isSubmitting ? onSubmitRequest : undefined}
        disabled={!isFormValid || isSubmitting}
        aria-label={isSubmitting ? submittingText : buttonText}
        sx={{
          backgroundColor: isFormValid
            ? theme.palette.primary.main
            : theme.palette.grey[400],
          color: 'white',
          px: 4,
          py: 2,
          textTransform: 'none',
          fontWeight: 'bold',
          opacity: isFormValid ? 1 : 0.6,
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
          '&:hover': {
            backgroundColor: isFormValid
              ? theme.palette.primary.dark
              : theme.palette.grey[400],
          },
          '&:disabled': {
            backgroundColor: theme.palette.grey[400],
            color: theme.palette.primary.contrastText,
            opacity: 0.6,
          },
        }}
      >
        {isSubmitting ? submittingText : buttonText}
      </Button>
    </Box>
  );

  if (isLoading) {
    return renderLoadingState();
  }

  if (error) {
    return renderErrorState();
  }

  return (
    <Paper
      component="section"
      elevation={1}
      aria-label="Budget edit section"
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: 'grey.200',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          mb: 3,
          flexShrink: 0,
        }}
      >
        Add Budget Item
      </Typography>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isSmallDesktop ? renderBudgetEditCards() : renderBudgetEditTable()}
      </Box>

      {showSubmitButton && (
        <Box
          sx={{
            flexShrink: 0,
            mt: 0,
          }}
        >
          {renderSubmitButton()}
        </Box>
      )}
    </Paper>
  );
}
